import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save, X, Loader2, Users, Upload, Image, Award, Star, Edit } from "lucide-react";
import { getImageUrl, DEFAULT_COACH_IMAGE } from "@/lib/imageUtils";

const CoachesManager = () => {
  const { authAxios } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingCoach, setEditingCoach] = useState(null); // Track which coach is being edited
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    title: "",
    specialty: "",
    bio: "",
    image_url: "",
    experience_years: "",
    achievements: "",
    quote: "",
    is_head_coach: false
  });

  useEffect(() => {
    fetchCoaches();
  }, []);

  const fetchCoaches = async () => {
    try {
      const response = await axios.get(`${API}/coaches`);
      setCoaches(response.data);
    } catch (e) {
      toast.error("Failed to fetch coaches");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('category', 'coaches');

      const response = await authAxios.post('/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fullUrl = `${API.replace('/api', '')}${response.data.url}`;
      setFormData({ ...formData, image_url: fullUrl });
      toast.success("Coach photo uploaded!");
    } catch (e) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        title: formData.title,
        specialty: formData.specialty,
        bio: formData.bio || null,
        image_url: formData.image_url || null,
        experience_years: formData.experience_years ? parseInt(formData.experience_years) : null,
        achievements: formData.achievements ? formData.achievements.split(',').map(a => a.trim()).filter(a => a) : [],
        quote: formData.quote || null,
        is_head_coach: formData.is_head_coach
      };
      
      if (editingCoach) {
        // Update existing coach
        await authAxios.put(`/coaches/${editingCoach.id}`, payload);
        toast.success("Coach updated successfully!");
      } else {
        // Create new coach
        await authAxios.post('/coaches', payload);
        toast.success("Coach added successfully!");
      }
      
      setShowForm(false);
      setEditingCoach(null);
      resetForm();
      fetchCoaches();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${editingCoach ? 'update' : 'add'} coach`);
    }
  };

  const handleEdit = (coach) => {
    setEditingCoach(coach);
    setFormData({
      name: coach.name || "",
      title: coach.title || "",
      specialty: coach.specialty || "",
      bio: coach.bio || "",
      image_url: coach.image_url || "",
      experience_years: coach.experience_years ? String(coach.experience_years) : "",
      achievements: Array.isArray(coach.achievements) ? coach.achievements.join(", ") : "",
      quote: coach.quote || "",
      is_head_coach: coach.is_head_coach || false
    });
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingCoach(null);
    resetForm();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this coach?")) return;
    
    try {
      await authAxios.delete(`/coaches/${id}`);
      toast.success("Coach deleted");
      fetchCoaches();
    } catch (e) {
      toast.error("Failed to delete coach");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      title: "",
      specialty: "",
      bio: "",
      image_url: "",
      experience_years: "",
      achievements: "",
      quote: "",
      is_head_coach: false
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-orange-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-orange-400" />
          Coaching Staff ({coaches.length})
        </h2>
        <button
          onClick={() => showForm ? handleCancelForm() : setShowForm(true)}
          data-testid="add-coach-btn"
          className="cyber-btn py-2 px-4 text-xs border-orange-400 text-orange-400 hover:bg-orange-400 hover:text-black"
        >
          <span className="flex items-center gap-2">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "CANCEL" : "ADD COACH"}
          </span>
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="cyber-card p-6 space-y-4 border-orange-400/30" data-testid="coach-form">
          {/* Form Header */}
          <div className="flex items-center gap-2 pb-3 border-b border-white/10">
            {editingCoach ? (
              <>
                <Edit className="w-5 h-5 text-neon-blue" />
                <h3 className="font-orbitron text-sm text-neon-blue">EDIT COACH: {editingCoach.name}</h3>
              </>
            ) : (
              <>
                <Plus className="w-5 h-5 text-orange-400" />
                <h3 className="font-orbitron text-sm text-orange-400">ADD NEW COACH</h3>
              </>
            )}
          </div>
          
          {/* Photo Upload Section */}
          <div className="border border-dashed border-orange-400/50 p-4">
            <Label className="font-orbitron text-sm text-orange-400 mb-3 flex items-center gap-2">
              <Upload className="w-4 h-4" />
              COACH PHOTO
            </Label>
            
            <div className="flex items-start gap-4">
              {/* Preview */}
              <div className="w-32 h-40 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                {formData.image_url ? (
                  <img src={getImageUrl(formData.image_url)} alt="Coach Preview" className="w-full h-full object-cover" />
                ) : (
                  <Users className="w-10 h-10 text-gray-600" />
                )}
              </div>

              <div className="flex-1 space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="coach-photo-upload"
                />
                <label
                  htmlFor="coach-photo-upload"
                  data-testid="coach-upload-btn"
                  className={`cyber-btn py-3 px-4 text-xs cursor-pointer inline-flex items-center gap-2 border-orange-400 text-orange-400 hover:bg-orange-400 hover:text-black ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      UPLOADING...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      UPLOAD PHOTO
                    </>
                  )}
                </label>
                <p className="font-rajdhani text-xs text-gray-500">
                  Recommended: Portrait orientation, 400x500px or larger
                </p>
                <div className="space-y-1">
                  <p className="font-rajdhani text-xs text-gray-400">Or paste image URL</p>
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                    placeholder="https://..."
                    className="bg-void-black border-white/10 text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Coach Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="Coach Mike"
                className="bg-void-black border-white/10"
                data-testid="coach-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
                placeholder="Strength & Conditioning Coach"
                className="bg-void-black border-white/10"
                data-testid="coach-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Specialty *</Label>
              <Input
                value={formData.specialty}
                onChange={(e) => setFormData({...formData, specialty: e.target.value})}
                required
                placeholder="Speed Training, Agility"
                className="bg-void-black border-white/10"
                data-testid="coach-specialty-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Years Experience</Label>
              <Input
                type="number"
                value={formData.experience_years}
                onChange={(e) => setFormData({...formData, experience_years: e.target.value})}
                placeholder="10"
                className="bg-void-black border-white/10"
              />
            </div>
          </div>

          {/* Quote */}
          <div className="space-y-2">
            <Label className="font-orbitron text-xs text-gray-400">Signature Quote</Label>
            <Input
              value={formData.quote}
              onChange={(e) => setFormData({...formData, quote: e.target.value})}
              placeholder="Hard work beats talent when talent doesn't work hard."
              className="bg-void-black border-white/10"
            />
          </div>

          {/* Bio */}
          <div className="space-y-2">
            <Label className="font-orbitron text-xs text-gray-400">Bio</Label>
            <Textarea
              value={formData.bio}
              onChange={(e) => setFormData({...formData, bio: e.target.value})}
              placeholder="Brief bio about the coach..."
              rows={3}
              className="bg-void-black border-white/10 font-rajdhani"
            />
          </div>

          {/* Achievements */}
          <div className="space-y-2">
            <Label className="font-orbitron text-xs text-gray-400">Achievements (comma separated)</Label>
            <Input
              value={formData.achievements}
              onChange={(e) => setFormData({...formData, achievements: e.target.value})}
              placeholder="CSCS Certified, D1 Experience, NFL Camp"
              className="bg-void-black border-white/10"
            />
          </div>

          {/* Head Coach Toggle */}
          <div className="flex items-center gap-3 p-3 bg-white/5 border border-white/10">
            <Switch
              checked={formData.is_head_coach}
              onCheckedChange={(v) => setFormData({...formData, is_head_coach: v})}
            />
            <div>
              <Label className="font-orbitron text-sm text-orange-400">HEAD COACH</Label>
              <p className="font-rajdhani text-xs text-gray-500">Mark this coach as the head/lead coach</p>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <button 
              type="button" 
              onClick={handleCancelForm}
              className="cyber-btn py-2 px-6 text-xs border-gray-500 text-gray-400 hover:bg-gray-500 hover:text-black"
            >
              <span className="flex items-center gap-2">
                <X className="w-4 h-4" />
                CANCEL
              </span>
            </button>
            <button 
              type="submit" 
              className={`cyber-btn py-2 px-6 text-xs ${editingCoach ? 'border-neon-blue text-neon-blue hover:bg-neon-blue' : 'border-orange-400 text-orange-400 hover:bg-orange-400'} hover:text-black`}
              data-testid="save-coach-btn"
            >
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                {editingCoach ? 'UPDATE COACH' : 'SAVE COACH'}
              </span>
            </button>
          </div>
        </form>
      )}

      {/* Coaches Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {coaches.map(coach => (
          <div key={coach.id} className={`cyber-card overflow-hidden ${coach.is_head_coach ? 'ring-2 ring-orange-400' : ''}`} data-testid={`coach-item-${coach.id}`}>
            <div className="aspect-[3/4] bg-white/5 relative">
              {coach.image_url ? (
                <img 
                  src={getImageUrl(coach.image_url)} 
                  alt={coach.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = DEFAULT_COACH_IMAGE;
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Users className="w-16 h-16 text-gray-700" />
                </div>
              )}
              
              {/* Head Coach Badge */}
              {coach.is_head_coach && (
                <div className="absolute top-2 left-2 bg-orange-400 px-2 py-1 flex items-center gap-1">
                  <Star className="w-3 h-3 text-black fill-current" />
                  <span className="font-orbitron text-xs text-black">HEAD COACH</span>
                </div>
              )}
              
              {/* Experience Badge */}
              {coach.experience_years && (
                <div className="absolute top-2 right-2 bg-black/70 px-2 py-1">
                  <span className="font-orbitron text-xs text-white">{coach.experience_years} YRS</span>
                </div>
              )}
            </div>
            
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-orbitron text-sm text-white truncate">{coach.name}</h3>
                  <p className="font-rajdhani text-xs text-neon-blue truncate">{coach.title}</p>
                  <p className="font-rajdhani text-xs text-gray-500 mt-1">{coach.specialty}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleEdit(coach)}
                    className="text-gray-500 hover:text-neon-blue transition-colors p-2 hover:bg-neon-blue/10 rounded"
                    data-testid={`edit-coach-${coach.id}`}
                    title="Edit coach"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(coach.id)}
                    className="text-gray-500 hover:text-red-500 transition-colors p-2 hover:bg-red-500/10 rounded"
                    data-testid={`delete-coach-${coach.id}`}
                    title="Delete coach"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {/* Achievements */}
              {coach.achievements && coach.achievements.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {coach.achievements.slice(0, 2).map((achievement, i) => (
                    <span 
                      key={i}
                      className="px-2 py-0.5 bg-neon-green/10 border border-neon-green/30 font-rajdhani text-[10px] text-neon-green"
                    >
                      {achievement}
                    </span>
                  ))}
                  {coach.achievements.length > 2 && (
                    <span className="px-2 py-0.5 bg-white/5 font-rajdhani text-[10px] text-gray-500">
                      +{coach.achievements.length - 2} more
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {coaches.length === 0 && (
        <div className="text-center py-12 cyber-card" data-testid="empty-coaches-state">
          <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="font-rajdhani text-gray-500">No coaches yet. Add your coaching staff!</p>
        </div>
      )}
    </div>
  );
};

export default CoachesManager;
